import { useState, useMemo } from "react"
import EyeHideIcon from "components/icons/EyeHideIcon"
import EyeIcon from "components/icons/EyeIcon"
import { useLang } from "hooks/useLang"

interface IProps extends React.InputHTMLAttributes<HTMLInputElement> {
    onChange: React.ChangeEventHandler<HTMLInputElement>
    inputID?: string
    label?: string
    name: string
    value?: string | number | readonly string[] | undefined
    error?: string
    reqStar?: boolean
    labelClassName?: string
    disableCapitalize?: boolean
    type:
        | "text"
        | "email"
        | "password"
        | "date"
        | "radio"
        | "tel"
        | "number"
        | "checkbox"
        | "textarea"
        | "phoneOrEmail"
}

const InputNew = ({
    onChange,
    inputID,
    label,
    type,
    name,
    value,
    error,
    reqStar = false,
    labelClassName,
    disableCapitalize = false,
    required, // destructure the required prop
    ...props
}: IProps) => {
    const [showPassword, setShowPassword] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const { t } = useLang()

    const handleFocus = () => {
        setIsFocused(true)
    }

    const handleBlur = () => {
        setIsFocused(false)
    }

    const style = useMemo(() => {
        return {
            label: `text-sm ${disableCapitalize ? "" : "capitalize"} transition-all md:text-base pb-1 text-gray-600 dark:text-gray-200 ${
                required || reqStar
                    ? "after:content-['*'] after:ml-1 after:text-red-500"
                    : ""
            } ${labelClassName ? labelClassName : ""}`,
            inputContainer: `flex border rounded-md transition-all h-9 overflow-hidden inputContainer bg-white dark:bg-opacity-20 bg-opacity-40 dark:bg-primary-theme-500 ${
                isFocused ? "border-blue-500" : "border-gray-300"
            } ${error ? "errorStyle" : ""}`,
            passwordEye: `flex w-9 h-full cursor-pointer items-center justify-center hover:bg-gray-100 hover:dark:bg-primary-theme-400 transition-all`,
        }
    }, [error, isFocused, disableCapitalize, labelClassName, reqStar, required])

    const setType = () => {
        if (type === "password" && showPassword) {
            return "text"
        } else {
            return type
        }
    }

    if (type === "checkbox") {
        return (
            <input
                onChange={onChange}
                type="checkbox"
                style={{ backgroundColor: "red" }}
                className="h-4 w-4 cursor-pointer"
            />
        )
    }

    return (
        <div className="flex w-full flex-col">
            <label
                className={style.label}
                htmlFor={inputID}>
                {label}
            </label>
            <div className={style.inputContainer}>
                <input
                    className="w-full bg-transparent p-2 text-sm"
                    value={value}
                    onChange={onChange}
                    id={inputID}
                    type={setType()}
                    name={name}
                    data-cy={name}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    required={required}
                    {...props}
                />
                {type === "password" ? (
                    <div
                        className={style.passwordEye}
                        onClick={() => setShowPassword(!showPassword)}>
                        {!showPassword ? <EyeIcon /> : <EyeHideIcon />}
                    </div>
                ) : null}
            </div>
            {error ? (
                <span className="pt-1 text-xs text-red-500 dark:text-red-400">
                    {t(error)}
                </span>
            ) : null}
        </div>
    )
}

export default InputNew
